import {
  AddIcon,
  Badge,
  Button,
  CancelIcon,
  DeleteIcon,
  EditIcon,
  Form,
  FormField,
  Input,
  ModalDialog,
  RedoIcon,
  Select,
  Switch,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { UserLight } from "../../http/types/users";
import { getUILanguages, Language, Paginated, PaginationQuery } from "../../types";
import { emailRegex, formatTimeStamp } from "../../utils";
import { BooleanValue } from "../ds2/BooleanValue";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Icon from "../ui/Icon";
import Table from "../ui/Table";
import SharingRequests, { SharingRequestsApi } from "./SharingRequests";

export type SharingDialogSharing = {
  user: UserLight;
  edit: boolean;
  // inherited from the parent project: managed there, not here
  viaProject?: boolean;
};

export type SharingDialogInvite = {
  id: string;
  email: string;
  sentAt: string;
  acceptedAt?: string;
  rejectedAt?: string;
  cancelled: boolean;
  edit: boolean;
};

export type SharingDialogInsertBody = {
  userEmail: string;
  language: string;
  edit: boolean;
};

type SearchResult<T> = { data?: Paginated<T>; error?: CustomError };

/** The shared entity (project, calculation) sharing calls, already bound to the entity. */
export type SharingDialogApi = SharingRequestsApi & {
  useSharings:(query: PaginationQuery, signal: AbortSignal) => SearchResult<SharingDialogSharing>;
  useInvites: (query: PaginationQuery, signal: AbortSignal) => SearchResult<SharingDialogInvite>;
  // a registered user gets the sharing at once, any other email an invite (`invited`)
  insertSharing: (body: SharingDialogInsertBody) => Promise<{ invited: boolean }>;
  updateSharing: (sharing: SharingDialogSharing, edit: boolean) => Promise<void>;
  deleteSharing: (sharing: SharingDialogSharing) => Promise<void>;
  resendInvite: (inviteId: string) => Promise<void>;
  cancelInvite: (inviteId: string) => Promise<void>;
  deleteInvite: (inviteId: string) => Promise<void>;
};

export type SharingDialogLabels = {
  title: string;
  shareWithUser: string;
  invitations: string;
  unshareQuestion: string;
};

interface SharingDialogProps {
  api: SharingDialogApi;
  labels: SharingDialogLabels;
  onClose: () => void;
}

interface EditForm {
  edit: boolean;
}

interface InviteForm {
  userEmail: string;
  edit: boolean;
  language?: Language;
}

const isPending = (invite: SharingDialogInvite) => !invite.acceptedAt && !invite.rejectedAt && !invite.cancelled;

const SharingDialog: FC<SharingDialogProps> = ({ api, labels, onClose }) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  const languages = getUILanguages(i18n.translation);
  // prefixed: the dialog opens over a list, whose own filters use the unprefixed url parameters
  const filters = useListFilters({ isSearchable: true, prefix: "sharings" });
  const invitesFilters = useListFilters({ prefix: "sharingInvites" });
  const aborter = useRef(new AbortController());
  const invitesAborter = useRef(new AbortController());
  const [sharingToEdit, setSharingToEdit] = useState<SharingDialogSharing>();
  const [sharingToDelete, setSharingToDelete] = useState<SharingDialogSharing>();
  const [inviteToCancel, setInviteToCancel] = useState<SharingDialogInvite>();
  const [inviteToDelete, setInviteToDelete] = useState<SharingDialogInvite>();
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedQ = useDebounce(filters.state.q);
  const debouncedInvitesQ = useDebounce(invitesFilters.state.q);

  const { data, error } = api.useSharings({ ...filters.state, q: debouncedQ }, aborter.current.signal);
  const { data: invitesData, error: invitesError } = api.useInvites(
    { ...invitesFilters.state, q: debouncedInvitesQ },
    invitesAborter.current.signal,
  );

  const submit = async (action: () => Promise<unknown>, onSuccess: () => void, message?: string) => {
    setIsSubmitting(true);
    try {
      await action();
      dispatch(showSuccessNotification(i18n.translation, message));
      onSuccess();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onEditSubmit = () => async (formData: EditForm) => {
    if (sharingToEdit) {
      await submit(
        () => api.updateSharing(sharingToEdit, formData.edit),
        () => setSharingToEdit(undefined),
      );
    }
  };

  const onInviteSubmit = () => async (formData: InviteForm) => {
    setIsSubmitting(true);
    try {
      const result = await api.insertSharing({
        userEmail: formData.userEmail,
        language: formData.language?.value ?? "",
        edit: formData.edit,
      });
      dispatch(
        showSuccessNotification(
          i18n.translation,
          result.invited ? i18n.translation.common.invitationHasBeenSentSuccessfully : undefined,
        ),
      );
      setInviteOpen(false);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInviteStatus = (invite: SharingDialogInvite) => {
    const status = i18n.translation.projects.invitationStatus;
    return invite.acceptedAt
      ? status.accepted
      : invite.rejectedAt
        ? status.rejected
        : invite.cancelled
          ? status.cancelled
          : status.pending;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        fitContent
        title={labels.title}
        postTitleButton={
          <Button onClick={() => setInviteOpen(true)}>
            <Icon glyph={AddIcon} />
            {i18n.translation.users.invite}
          </Button>
        }
      >
        <Table<SharingDialogSharing>
          filters={filters}
          search={{
            placeholder: i18n.translation.users.search,
            onChange: (term) => {
              aborter.current.abort();
              aborter.current = new AbortController();
              invitesAborter.current.abort();
              invitesAborter.current = new AbortController();
              // the search filters the invitations as well, from their first page
              invitesFilters.setSearchTerm(term);
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.users.usersName}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.users.usersEmail}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.writePermissions}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <TableCell>
                {item.user.firstName} {item.user.lastName}{" "}
                {item.viaProject && <Badge color="info">{i18n.translation.common.sharedViaProject}</Badge>}
              </TableCell>
              <TableCell>{item.user.email}</TableCell>
              <TableCell align="center">
                <BooleanValue value={item.edit} />
              </TableCell>
            </>
          )}
          rowActions={(item) =>
            item.viaProject
              ? []
              : [
                  {
                    label: i18n.translation.common.edit,
                    onClick: () => setSharingToEdit(item),
                    icon: <Icon glyph={EditIcon} />,
                  },
                  {
                    label: i18n.translation.common.remove,
                    onClick: () => setSharingToDelete(item),
                    icon: <Icon glyph={DeleteIcon} />,
                    separatorBefore: true,
                    variant: "danger",
                  },
                ]
          }
          total={data?.total}
          error={!!error}
        />

        {/* the search filters the requests as well */}
        <SharingRequests api={api} searchTerm={debouncedQ} />

        {!!invitesData?.items.length && (
          <>
            <h4>{labels.invitations}</h4>
            <Table<SharingDialogInvite>
              filters={invitesFilters}
              head={
                <TableRow>
                  <TableHeadCell align="left">{i18n.translation.users.usersEmail}</TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.common.sentAt}</TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.common.status}</TableHeadCell>
                  <TableHeadCell align="center">{i18n.translation.common.writePermissions}</TableHeadCell>
                  <TableHeadCell></TableHeadCell>
                </TableRow>
              }
              items={invitesData.items}
              renderRow={(item) => (
                <>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>{formatTimeStamp(item.sentAt, auth.identity?.language)}</TableCell>
                  <TableCell>{getInviteStatus(item)}</TableCell>
                  <TableCell align="center">
                    <BooleanValue value={item.edit} />
                  </TableCell>
                </>
              )}
              rowActions={(item) => [
                {
                  label: i18n.translation.common.cancel,
                  onClick: () => setInviteToCancel(item),
                  disabled: isSubmitting || !isPending(item),
                  icon: <Icon glyph={CancelIcon} />,
                },
                {
                  label: i18n.translation.common.resendInvite,
                  onClick: () =>
                    submit(
                      () => api.resendInvite(item.id),
                      () => undefined,
                      i18n.translation.common.invitationHasBeenSentSuccessfully,
                    ),
                  disabled: isSubmitting || !isPending(item),
                  icon: <Icon glyph={RedoIcon} />,
                },
                {
                  label: i18n.translation.common.remove,
                  onClick: () => setInviteToDelete(item),
                  icon: <Icon glyph={DeleteIcon} />,
                  separatorBefore: true,
                  variant: "danger",
                },
              ]}
              total={invitesData.total}
              error={!!invitesError}
            />
          </>
        )}
      </ModalDialog>

      {!!sharingToDelete && (
        <ModalDialog
          onClose={() => setSharingToDelete(undefined)}
          title={i18n.translation.users.remove}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key={0} onClick={() => setSharingToDelete(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key={1}
              onClick={() =>
                submit(
                  () => api.deleteSharing(sharingToDelete),
                  () => setSharingToDelete(undefined),
                )
              }
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {labels.unshareQuestion}
        </ModalDialog>
      )}

      {!!inviteToCancel && (
        <ModalDialog
          onClose={() => setInviteToCancel(undefined)}
          title={i18n.translation.common.cancel}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key={0} onClick={() => setInviteToCancel(undefined)} variant="secondary">
              {i18n.translation.common.no}
            </Button>,
            <Button
              key={1}
              onClick={() =>
                submit(
                  () => api.cancelInvite(inviteToCancel.id),
                  () => setInviteToCancel(undefined),
                )
              }
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {i18n.translation.common.cancelInviteQuestion}
        </ModalDialog>
      )}

      {!!inviteToDelete && (
        <ModalDialog
          onClose={() => setInviteToDelete(undefined)}
          title={i18n.translation.common.delete}
          isSubmitting={isSubmitting}
          buttons={[
            <Button key={0} onClick={() => setInviteToDelete(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key={1}
              onClick={() =>
                submit(
                  () => api.deleteInvite(inviteToDelete.id),
                  () => setInviteToDelete(undefined),
                )
              }
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {i18n.translation.common.deleteInviteQuestion}
        </ModalDialog>
      )}

      {!!sharingToEdit && (
        <ModalDialog onClose={() => setSharingToEdit(undefined)} title={i18n.translation.projects.editPermissions}>
          <Form<EditForm>
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            onSubmit={onEditSubmit}
            defaultValues={{ edit: sharingToEdit.edit }}
            secondaryButton={
              <Button onClick={() => setSharingToEdit(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
          >
            {({ control }) => (
              <Controller
                name="edit"
                control={control}
                render={({ field }) => (
                  <FormField label={i18n.translation.common.writePermissions}>
                    {({ labelId }) => <Switch id={labelId} checked={field.value} onChange={field.onChange} />}
                  </FormField>
                )}
              />
            )}
          </Form>
        </ModalDialog>
      )}

      {isInviteOpen && (
        <ModalDialog title={labels.shareWithUser} onClose={() => setInviteOpen(false)}>
          <Form<InviteForm>
            defaultValues={{ userEmail: "", edit: false }}
            submitText={i18n.translation.common.invite}
            disabled={isSubmitting}
            secondaryButton={
              <Button onClick={() => setInviteOpen(false)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={onInviteSubmit}
          >
            {({ control }) => (
              <FormFieldsContainer fullWidth>
                <Controller
                  name="userEmail"
                  control={control}
                  rules={{
                    required: true,
                    pattern: { value: emailRegex, message: i18n.translation.common.fieldInvalidValue },
                  }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.users.usersEmail} error={fieldState.error}>
                      {({ labelId, isInvalid }) => (
                        <Input
                          id={labelId}
                          isInvalid={isInvalid}
                          autoComplete="on"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="language"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.translation.common.languages.singular} error={fieldState.error}>
                      {({ labelId }) => (
                        <Select
                          {...field}
                          inputId={labelId}
                          options={languages}
                          getOptionLabel={(x) => x.display}
                          onChange={(value) => field.onChange(value ?? undefined)}
                        />
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="edit"
                  control={control}
                  render={({ field }) => (
                    <FormField label={i18n.translation.common.writePermissions}>
                      {({ labelId }) => <Switch id={labelId} checked={field.value} onChange={field.onChange} />}
                    </FormField>
                  )}
                />
              </FormFieldsContainer>
            )}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default SharingDialog;
